<template>
	<Modal
		v-if="showModal"
		:allow-close="true"
		:max-width="'max-w-[485px]'"
		:hide-actions="true"
		:close-button-absolute="true"
		:prevent-scrolling="false"
		@modal:close="closeModal"
	>
		<template #body>
			<reason-step
				v-if="step === REASON_STEP"
				@select="handleReasonSelected"
			/>

			<celebration-step
				v-else-if="step === CELEBRATION_STEP"
				:is-submitting="isSubmitting"
				@continue="handleCelebrationContinue"
			/>

			<offer-step
				v-else-if="step === OFFER_STEP"
				:is-submitting="isSubmitting"
				@accept="handleOfferAccept"
				@decline="declineToFeedback"
			/>

			<bridge-step
				v-else-if="step === BRIDGE_STEP"
				@open-settings="handleOpenRecommendationSettings"
				@cancel="declineToFeedback"
			/>

			<usage-impact-step
				v-else-if="step === USAGE_IMPACT_STEP"
				:features="usageFeatures"
				:is-loading="isLoadingUsage"
				@keep="closeModal"
				@cancel="enterOffer"
			/>

			<discount-applied-step
				v-else-if="step === DISCOUNT_APPLIED_STEP"
				:next-payment-date="discount.next_payment_date"
				:discounted-amount="discount.discounted_amount"
				:regular-amount="discount.regular_amount"
				:currency="discount.currency"
				:discount-months="discount.discount_months"
				@dismiss="closeModal"
			/>

			<feedback-step
				v-else-if="step === FEEDBACK_STEP"
				:title="feedbackTitle"
				:description="feedbackDescription"
				:placeholder="feedbackPlaceholder"
				:is-submitting="isSubmitting"
				@submit="handleFeedbackSubmit"
			/>

			<confirmation-step
				v-else-if="step === CONFIRMATION_STEP"
				:ends-at="endsAt"
				:encouragement="encouragement"
				:is-reactivating="isReactivating"
				@dismiss="closeModal"
				@reactivate="emit('reactivate')"
			/>
		</template>
	</Modal>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import Modal from '../../../modal';
import { getUsageSummary, trackCancellationReasonSelected } from '../../../../api/subscription';
import { useUserEvents, USER_EVENTS } from '../../../../store/user-events.store';
import { RECOMMENDATION_SETTINGS_PARAM } from '../../../../jobboard-v3/composables/useSettingsDeepLink';
import ReasonStep from './reason-step.vue';
import CelebrationStep from './celebration-step.vue';
import ConfirmationStep from './confirmation-step.vue';
import OfferStep from './offer-step.vue';
import DiscountAppliedStep from './discount-applied-step.vue';
import FeedbackStep from './feedback-step.vue';
import BridgeStep from './bridge-step.vue';
import UsageImpactStep from './usage-impact-step.vue';
import {
	BRANCH_ENTRY_STEP,
	BRIDGE_STEP,
	CELEBRATION_STEP,
	CONFIRMATION_STEP,
	DISCOUNT_APPLIED_STEP,
	ENCOURAGEMENT_BY_REASON,
	FEEDBACK_DESCRIPTION_BY_REASON,
	FEEDBACK_PLACEHOLDER_BY_REASON,
	FEEDBACK_STEP,
	FEEDBACK_TITLE_BY_REASON,
	OFFER_STEP,
	REASON_STEP,
	USAGE_IMPACT_STEP,
} from './reasons';

const props = defineProps({
	showModal: {
		type: Boolean,
		default: false,
	},
	closeModal: {
		type: Function,
		required: true,
	},
	/**
	 * The parent's cancel handler, which owns the auth/store updates the response drives.
	 * Resolves to the cancel endpoint's payload, or to nothing once it has reported a failure.
	 */
	cancelSubscription: {
		type: Function,
		required: true,
	},
	/**
	 * The parent's retention-offer handler, which owns the auth/store updates the response
	 * drives. Resolves to the accept endpoint's payload, or to nothing once it has reported
	 * a failure.
	 */
	acceptRetentionOffer: {
		type: Function,
		required: true,
	},
	/**
	 * Whether the 30%-off offer may be surfaced, from the user's `retention_offer_eligible`.
	 * Suppressed by EN-880's 12-cumulative-paid-months cooldown, among other guards.
	 */
	isOfferEligible: {
		type: Boolean,
		default: false,
	},
	/**
	 * Whether the user has the Recommendation Settings drawer the bridge screen sends them to,
	 * from their `criteria-weighted-search` access. The drawer only renders behind that flag,
	 * so without it the bridge would promise a screen that never opens.
	 */
	canConfigureCriteria: {
		type: Boolean,
		default: false,
	},
	isReactivating: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits(['reactivate']);

const router = useRouter();
const userEvents = useUserEvents();

const step = ref(REASON_STEP);
const selectedReason = ref(null);
const isSubmitting = ref(false);
const endsAt = ref(null);

// Whether this session actually put the offer in front of the user, which is what the
// cancellation record means by `offer_shown` — not merely whether they were eligible.
const offerShown = ref(false);
// The accept endpoint's figures, rendered by the discount-applied popup.
const discount = ref(null);

// The usage-summary endpoint's ranked features, rendered by the usage-impact screen.
const usageFeatures = ref([]);
const isLoadingUsage = ref(false);

// Bumped whenever the modal closes, so a cancelSubscription or acceptRetentionOffer call
// still in flight from a prior session can tell it's stale once it resolves.
const cancellationSession = ref(0);

const encouragement = computed(() => ENCOURAGEMENT_BY_REASON[selectedReason.value] ?? '');
const feedbackPlaceholder = computed(() => FEEDBACK_PLACEHOLDER_BY_REASON[selectedReason.value] ?? '');
const feedbackTitle = computed(() => FEEDBACK_TITLE_BY_REASON[selectedReason.value] ?? '');
const feedbackDescription = computed(() => FEEDBACK_DESCRIPTION_BY_REASON[selectedReason.value] ?? '');

/**
 * EN-879's reason-selected event, in its three destinations. None of them may block the
 * step transition, so the one call that rethrows is caught here.
 *
 * @param {string} reason - A SubscriptionCancellationReason value
 */
const trackReasonSelected = (reason) => {
	trackCancellationReasonSelected(reason);
	userEvents.trackEvent(USER_EVENTS.CANCELLATION_REASON_SELECTED, { reason }).catch(() => {});

	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push({ event: 'cancellation_reason_selected', reason });
};

/**
 * Two branches open on a screen the user may not be able to act on, and both fall back to
 * feedback rather than promising something that cannot be delivered:
 *
 * - the offer is withheld from some users (EN-880's cooldown, a comped account, no coupon
 *   configured), and the accept endpoint would reject the discount they were shown;
 * - the bridge's Recommendation Settings drawer only exists behind `criteria-weighted-search`.
 *
 * @param {string} entryStep - The branch's own first step, from BRANCH_ENTRY_STEP
 * @returns {string} The step to actually show
 */
const resolveEntryStep = (entryStep) => {
	if (entryStep === OFFER_STEP && !props.isOfferEligible) {
		return FEEDBACK_STEP;
	}

	if (entryStep === BRIDGE_STEP && !props.canConfigureCriteria) {
		return FEEDBACK_STEP;
	}

	return entryStep;
};

/**
 * The usage-impact branch's move onto the offer, which is where its `offer_shown` is settled —
 * unlike the price branch, these reasons only reach the offer if the user chose to go on
 * cancelling after seeing what they would lose.
 */
const enterOffer = () => {
	offerShown.value = props.isOfferEligible;
	step.value = resolveEntryStep(OFFER_STEP);
};

/**
 * The counts the usage-impact screen exists to show. With none of them there is nothing to
 * weigh, so a failed request moves the user on to the offer rather than leaving them on an
 * empty panel. A successful request always returns at least one feature.
 */
const loadUsageSummary = async () => {
	const session = cancellationSession.value;
	isLoadingUsage.value = true;

	try {
		const features = await getUsageSummary();

		if (session === cancellationSession.value) {
			usageFeatures.value = features;
		}
	} catch {
		// Only if the user is still waiting on those counts: a request that takes long enough
		// to time out can land after they have already moved themselves on.
		if (session === cancellationSession.value && step.value === USAGE_IMPACT_STEP) {
			enterOffer();
		}
	} finally {
		if (session === cancellationSession.value) {
			isLoadingUsage.value = false;
		}
	}
};

const handleReasonSelected = (reason) => {
	selectedReason.value = reason;
	trackReasonSelected(reason);

	const entryStep = BRANCH_ENTRY_STEP[reason];

	offerShown.value = entryStep === OFFER_STEP && props.isOfferEligible;
	step.value = resolveEntryStep(entryStep);

	if (step.value === USAGE_IMPACT_STEP) {
		loadUsageSummary();
	}
};

/**
 * Every branch ends the same way: cancel with whatever the branch collected, then hand
 * the plan's end date to the shared confirmation.
 *
 * @param {Object} payload - The branch's own cancel fields, merged over the reason
 */
const completeCancellation = async (payload) => {
	const session = cancellationSession.value;
	isSubmitting.value = true;

	try {
		const response = await props.cancelSubscription({ reason: selectedReason.value, ...payload });

		// The parent already surfaced the error notification, so stay on the branch screen.
		if (!response?.success) {
			return;
		}

		// EN-879's final-cancellation metadata. The branch supplies whatever of it applies;
		// the reason doubles as the branch, since each reason has exactly one branch.
		userEvents
			.trackEvent(USER_EVENTS.SUBSCRIPTION_CANCELLED, {
				reason: selectedReason.value,
				branch: selectedReason.value,
				offer_shown: offerShown.value,
				offer_accepted: payload.offer_accepted ?? false,
				feedback_provided: Boolean(payload.feedback),
			})
			.catch(() => {});

		// The modal may have been closed (and reopened) while this call was in flight;
		// a stale response must not clobber whatever the new session is now showing.
		if (session !== cancellationSession.value) {
			return;
		}

		endsAt.value = response.user?.current_plan?.ends_at;
		step.value = CONFIRMATION_STEP;
	} finally {
		if (session === cancellationSession.value) {
			isSubmitting.value = false;
		}
	}
};

const handleCelebrationContinue = (contactOptIn) => completeCancellation({ contact_opt_in: contactOptIn });

/**
 * Accepting the discount leaves the subscription exactly as it is and the popup closes the
 * flow — one of the two exits that never cancel, the other being the bridge.
 */
const handleOfferAccept = async () => {
	const session = cancellationSession.value;
	isSubmitting.value = true;

	try {
		const response = await props.acceptRetentionOffer();

		// The parent already surfaced the error notification, so stay on the offer screen.
		if (!response?.success) {
			return;
		}

		// The modal may have been closed (and reopened) while this call was in flight;
		// a stale response must not clobber whatever the new session is now showing.
		if (session !== cancellationSession.value) {
			return;
		}

		discount.value = response;
		step.value = DISCOUNT_APPLIED_STEP;
	} finally {
		if (session === cancellationSession.value) {
			isSubmitting.value = false;
		}
	}
};

// Turning down what the branch offered — the discount, or the bridge to Recommendation
// Settings — leaves the retention attempt behind and joins the shared feedback path.
const declineToFeedback = () => {
	step.value = FEEDBACK_STEP;
};

/**
 * The bridge's one non-cancelling exit: the flow closes for good (re-entry restarts at reason
 * selection) and the user lands in the drawer that fixes the filters they complained about.
 */
const handleOpenRecommendationSettings = () => {
	props.closeModal();
	router.push({ name: 'job-board-search', query: { [RECOMMENDATION_SETTINGS_PARAM]: '1' } });
};

// `offer_accepted` is always false here: accepting the discount ends the flow without
// cancelling, so a cancellation can only follow a decline, an offer that was suppressed, or a
// branch that never offered anything.
const handleFeedbackSubmit = (feedback) => completeCancellation({
	feedback: feedback || null,
	offer_shown: offerShown.value,
	offer_accepted: false,
});

// The flow never resumes mid-way: closing it and coming back starts at reason selection.
watch(() => props.showModal, (isOpen) => {
	if (!isOpen) {
		step.value = REASON_STEP;
		selectedReason.value = null;
		endsAt.value = null;
		offerShown.value = false;
		discount.value = null;
		usageFeatures.value = [];
		isLoadingUsage.value = false;
		isSubmitting.value = false;
		cancellationSession.value += 1;
	}
});
</script>
